import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Briefcase,
  Shield,
  Phone,
  Calendar as CalendarIcon,
  FolderLock,
  MessageSquare,
  ArrowLeft,
  LogOut,
  ChevronRight,
  Lock,
  Settings,
  Menu,  Users,
  XCircle,
  Handshake,
  Presentation,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { type ReactNode, useState } from "react";
import { NotificationBell } from "@/components/notification-bell";
import { isAccreditedForDealRoom } from "@shared/pipeline";

export interface ReferralAgentInfo {
  id: string;
  companyName: string;
  logoUrl: string | null;
  referralCode: string;
  website: string | null;
  // Task #1971 — when true the agent has hidden all consultation /
  // scheduling calls-to-action from the investors they registered.
  hideConsultationCtas?: boolean;
}

export function usePartnerAgent() {
  const { data } = useQuery<ReferralAgentInfo | null>({
    queryKey: ["/api/me/referral-agent"],
  });
  return data ?? null;
}

export function PartnerAgentContextStrip({
  context,
  className = "",
}: {
  context: "accreditation" | "consultations" | "deal-rooms" | "project" | "dashboard";
  className?: string;
}) {
  const agent = usePartnerAgent();
  if (!agent) return null;

  const labelMap = {
    accreditation: "Because you were referred — accreditation supported by",
    consultations: "Because you were referred — consultations coordinated through",
    "deal-rooms": "Because you were referred — deal-room access arranged by",
    project: "Because you were referred — brought to you by",
    dashboard: "Because you were referred by",
  } as const;

  const inner = (
    <div
      className={`relative flex items-center gap-3 overflow-hidden rounded-lg border border-primary/15 bg-gradient-to-r from-primary/[0.05] via-card to-brand-gold/[0.07] px-4 py-3 pl-5 shadow-sm ${className}`}
      data-testid={`partner-agent-strip-${context}`}
    >
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1 bg-brand-gold" />
      {agent.logoUrl ? (
        <img
          src={agent.logoUrl}
          alt={`${agent.companyName} logo`}
          className="h-8 w-auto max-w-[88px] object-contain shrink-0"
          data-testid="img-partner-agent-logo"
        />
      ) : (
        <div className="h-8 w-8 rounded-full bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 flex items-center justify-center shrink-0">
          <Users className="w-4 h-4" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-heading font-medium uppercase tracking-wide text-primary/70 leading-tight">
          {labelMap[context]}
        </p>
        <p
          className="text-sm font-semibold text-primary truncate"
          data-testid="text-partner-agent-name"
        >
          {agent.companyName}
        </p>
      </div>
    </div>
  );

  return agent.website ? (
    <a
      href={agent.website}
      target="_blank"
      rel="noopener noreferrer"
      className="block rounded-lg transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      data-testid="link-partner-agent-website"
    >
      {inner}
    </a>
  ) : (
    inner
  );
}

function ReferredByBadge({ agent, compact = false }: { agent: ReferralAgentInfo; compact?: boolean }) {
  const content = (
    <div
      className={`inline-flex items-center gap-2 rounded-md border border-primary/15 bg-gradient-to-r from-primary/[0.05] to-brand-gold/[0.10] ${compact ? "px-2 py-1" : "px-2.5 py-1.5"}`}
      data-testid="badge-referred-by"
    >
      <Users className="w-3 h-3 text-primary shrink-0" />
      <span className="text-[10px] font-heading font-medium uppercase tracking-wide text-primary/70 shrink-0">Referred by</span>
      {agent.logoUrl && (
        <img
          src={agent.logoUrl}
          alt={`${agent.companyName} logo`}
          className={`object-contain ${compact ? "max-h-4 max-w-[60px]" : "max-h-5 max-w-[80px]"}`}
          data-testid="img-referred-by-logo"
        />
      )}
      <span className={`text-primary font-semibold truncate ${compact ? "text-[11px] max-w-[100px]" : "text-xs max-w-[140px]"}`}>
        {agent.companyName}
      </span>
    </div>
  );
  return agent.website ? (
    <a
      href={agent.website}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex rounded-md transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {content}
    </a>
  ) : (
    content
  );
}

const investorNavItems = [
  { href: "/dashboard", label: "Overview", icon: LayoutDashboard, requiresAccreditation: false },
  { href: "/dashboard/projects", label: "Projects", icon: Briefcase, requiresAccreditation: false },
  { href: "/dashboard/inbox", label: "Inbox", icon: MessageSquare, requiresAccreditation: false },
  { href: "/dashboard/accreditation", label: "Accreditation", icon: Shield, requiresAccreditation: false },
  { href: "/dashboard/consultations", label: "Consultations", icon: Phone, requiresAccreditation: false },
  { href: "/dashboard/my-schedule", label: "My Schedule", icon: CalendarIcon, requiresAccreditation: false },
  { href: "/dashboard/deal-rooms", label: "Deal Rooms", icon: FolderLock, requiresAccreditation: false },
];

function getAccreditationComplete(status: string) {
  return isAccreditedForDealRoom(status);
}

function SidebarContent({
  user,
  location,
  isAccredited,
  logout,
  referralAgent,
  onNavigate,
}: {
  user: any;
  location: string;
  isAccredited: boolean;
  logout: () => void;
  referralAgent: ReferralAgentInfo | null;
  onNavigate?: () => void;
}) {
  // Task #976 — inbox unread count drives a small numeric badge on the
  // sidebar nav so the investor sees waiting replies at a glance.
  const { data: inboxUnread } = useQuery<{ count: number }>({
    queryKey: ["/api/investor/inbox/unread-count"],
    refetchInterval: 30_000,
    staleTime: 0,
  });
  const inboxUnreadCount = inboxUnread?.count ?? 0;

  const { data: partnerPrograms } = useQuery<{
    leadReferralEnabled?: boolean;
    whiteLabelEnabled?: boolean;
  }>({
    queryKey: ["/api/me/partner-programs"],
  });
  const showReferrals =
    partnerPrograms?.leadReferralEnabled === true ||
    partnerPrograms?.whiteLabelEnabled === true;
  const { data: presentationStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/investor-presentation/status"],
  });

  const navItems = [
    ...investorNavItems,
    ...(showReferrals
      ? [{ href: "/dashboard/referrals", label: "Referrals", icon: Handshake, requiresAccreditation: false }]
      : []),
    ...(presentationStatus?.enabled === true
      ? [{ href: "/dashboard/presentation", label: "Presentation", icon: Presentation, requiresAccreditation: false }]
      : []),
  ];

  return (
    <>
      <div className="relative p-4 border-b border-primary/10 bg-gradient-to-b from-primary/[0.04] to-transparent">
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-primary/70 to-brand-gold"
        />
        <Link
          href="/"
          className="inline-flex items-center gap-2 rounded-md text-gray-500 hover:text-primary transition-colors text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          data-testid="link-investor-back"
          onClick={onNavigate}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Site
        </Link>
        <div className="mt-3 flex items-center gap-3">
          <span
            aria-hidden="true"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm ring-1 ring-brand-gold/50"
          >
            <Shield className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className="font-heading font-bold text-primary text-lg leading-tight">Investor Portal</h2>
            <p className="text-xs text-gray-500 truncate">{user.email}</p>
          </div>
        </div>
        {referralAgent && (
          <div className="mt-3">
            <ReferredByBadge agent={referralAgent} />
          </div>
        )}
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="investor-nav">
        {navItems
          // Task #1971 — drop the consultation + scheduling nav items
          // entirely when the investor's registering agent has hidden them.
          .filter((item) =>
            referralAgent?.hideConsultationCtas
              ? item.href !== "/dashboard/consultations" && item.href !== "/dashboard/my-schedule"
              : true,
          )
          .map((item) => {
          const isActive = location === item.href ||
            (item.href !== "/dashboard" && location.startsWith(item.href)) ||
            // While inside a deal room (/deal-room/:id) keep the Deal Rooms
            // nav item highlighted so the sidebar reflects where the investor is.
            (item.href === "/dashboard/deal-rooms" && location.startsWith("/deal-room"));
          const isLocked = item.requiresAccreditation && !isAccredited;
          const showInboxBadge =
            item.href === "/dashboard/inbox" && inboxUnreadCount > 0;

          return (
            <Link key={item.href} href={isLocked ? "#" : item.href} onClick={isLocked ? undefined : onNavigate}>
              <button
                className={`relative w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors touch-target focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
                  isLocked
                    ? "text-gray-300 cursor-not-allowed"
                    : isActive
                      ? "bg-primary/[0.08] text-primary font-semibold shadow-sm ring-1 ring-inset ring-primary/15"
                      : "text-gray-600 hover:text-primary hover:bg-primary/[0.04]"
                }`}
                disabled={isLocked}
                aria-current={isActive && !isLocked ? "page" : undefined}
                data-testid={`investor-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {isActive && !isLocked && (
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-brand-gold"
                  />
                )}
                <item.icon className="w-5 h-5 shrink-0" />
                {item.label}
                {showInboxBadge && (
                  <span
                    className={`${isActive ? "" : "ml-auto"} inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold`}
                    data-testid="badge-sidebar-inbox"
                    aria-label={`${inboxUnreadCount} unread inbox threads`}
                  >
                    {inboxUnreadCount > 99 ? "99+" : inboxUnreadCount}
                  </span>
                )}
                {isLocked && <Lock className="w-3 h-3 ml-auto text-gray-300" />}
                {isActive && !isLocked && !showInboxBadge && <ChevronRight className="w-3 h-3 ml-auto" />}
              </button>
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t border-primary/10 space-y-2">
        <Link href="/dashboard/account" onClick={onNavigate}>
          <button
            className={`relative w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors touch-target focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
              location === "/dashboard/account"
                ? "bg-primary/[0.08] text-primary font-semibold shadow-sm ring-1 ring-inset ring-primary/15"
                : "text-gray-600 hover:text-primary hover:bg-primary/[0.04]"
            }`}
            aria-current={location === "/dashboard/account" ? "page" : undefined}
            data-testid="investor-nav-account-settings"
          >
            {location === "/dashboard/account" && (
              <span
                aria-hidden="true"
                className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-brand-gold"
              />
            )}
            <Settings className="w-5 h-5 shrink-0" />
            Account Settings
            {location === "/dashboard/account" && <ChevronRight className="w-3 h-3 ml-auto" />}
          </button>
        </Link>
        {!isAccredited && (() => {
          const status = user?.accreditationStatus ?? "none";
          const isRejected = status === "rejected";
          const isPending = status === "pending_verification";
          return (
            <Link
              href="/dashboard/accreditation"
              onClick={onNavigate}
              className={`relative block overflow-hidden rounded-lg border px-3 py-2.5 pl-4 transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                isRejected
                  ? "bg-red-50 border-red-200"
                  : "border-primary/20 bg-gradient-to-br from-primary/[0.06] via-card to-brand-gold/[0.12]"
              }`}
              data-testid="link-sidebar-eligibility-cta"
            >
              <span
                aria-hidden="true"
                className={`absolute inset-y-0 left-0 w-1 ${isRejected ? "bg-red-400" : "bg-brand-gold"}`}
              />
              <div className={`flex items-center gap-2.5 text-xs font-semibold ${isRejected ? "text-red-600" : "text-primary"}`}>
                <span
                  aria-hidden="true"
                  className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                    isRejected ? "bg-red-100 text-red-600" : "bg-primary text-primary-foreground ring-1 ring-brand-gold/50"
                  }`}
                >
                  {isRejected ? <XCircle className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                </span>
                <span className="flex-1 leading-snug">
                  {isPending
                    ? "Complete eligibility questionnaire"
                    : isRejected
                      ? "Update your eligibility"
                      : "Confirm eligibility to unlock deal rooms"}
                </span>
                <ChevronRight className="w-3.5 h-3.5 shrink-0 opacity-70" />
              </div>
            </Link>
          );
        })()}
        <button
          onClick={() => { logout(); onNavigate?.(); }}
          className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-gray-600 hover:text-primary hover:bg-primary/[0.04] transition-colors touch-target focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          data-testid="investor-logout"
        >
          <LogOut className="w-5 h-5" />
          Sign Out
        </button>
      </div>
    </>
  );
}

export function InvestorLayout({ children, fullBleed = false }: { children: ReactNode; fullBleed?: boolean }) {
  const [location] = useLocation();
  const { user, isLoading, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Must be called before any conditional returns below — React's
  // rules-of-hooks require hook order to be stable across renders.
  // Previously this lived after the `!user` / admin early returns,
  // which crashed InvestorLayout the moment a freshly-registered
  // user transitioned from `user=null` to a populated user object
  // ("Rendered more hooks than during the previous render").
  const { data: referralAgentData } = useQuery<ReferralAgentInfo | null>({
    queryKey: ["/api/me/referral-agent"],
    enabled: !!user && user.role !== "admin",
  });
  const referralAgent = referralAgentData ?? null;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <Lock className="w-16 h-16 text-primary mx-auto" />
          <h1 className="text-2xl font-heading font-bold text-gray-900">Sign In Required</h1>
          <p className="text-gray-500">Please sign in to access your investor portal.</p>
          <Link href="/login">
            <Button className="bg-primary hover:bg-primary/90 text-white font-semibold" data-testid="button-investor-login">
              Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (user.role === "admin") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <Shield className="w-16 h-16 text-primary mx-auto" />
          <h1 className="text-2xl font-heading font-bold text-gray-900">Admin Account</h1>
          <p className="text-gray-500">Admin accounts don't have an investor portal. Use the Admin Portal instead.</p>
          <Link href="/admin">
            <Button className="bg-primary hover:bg-primary/90 text-white font-semibold" data-testid="button-go-admin">
              Go to Admin Portal
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isAccredited = getAccreditationComplete(user.accreditationStatus || "none");

  const sidebarProps = { user, location, isAccredited, logout, referralAgent };

  // `investor-surface-light` pins card/popover/muted tokens to light values
  // so a leftover global `.dark` (the deal room forces it) can't paint navy
  // cards under navy headings. The deal room passes `fullBleed` and keeps
  // its content area unpinned; its sidebar and mobile bar are still pinned.
  return (
    <div className={`theme-investor min-h-screen bg-gray-50${fullBleed ? "" : " investor-surface-light"}`}>
      <div className="investor-surface-light sticky top-0 z-50 bg-white border-b border-primary/10 px-4 pb-3 pt-safe flex items-center gap-3 lg:hidden">
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-primary/70 to-brand-gold"
        />
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="shrink-0 touch-target" data-testid="investor-mobile-menu">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="theme-investor investor-surface-light w-[280px] p-0 bg-white flex flex-col">
            <SidebarContent {...sidebarProps} onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <h1 className="font-heading font-bold text-primary text-base truncate flex-1">Investor Portal</h1>
        <NotificationBell inboxHref="/dashboard/notifications/inbox" preferencesHref="/dashboard/notifications" />
        {referralAgent && (
          <div className="shrink-0">
            <ReferredByBadge agent={referralAgent} compact />
          </div>
        )}
      </div>

      <div className="flex">
        <aside className="investor-surface-light hidden lg:flex w-64 bg-white border-r border-primary/10 flex-col fixed h-screen z-40" data-testid="investor-sidebar">
          <SidebarContent {...sidebarProps} />
        </aside>

        <main className="flex-1 lg:ml-64 min-h-screen flex flex-col" data-testid="investor-content">
          {/* fullBleed lets a child (e.g. the deal room) supply its own
              sticky header and own the whole content area — so we drop the
              desktop notification bar and the default padding to avoid a
              double header / extra gutter. */}
          {!fullBleed && (
            <div className="hidden lg:flex sticky top-0 z-40 bg-white border-b border-gray-200 px-6 py-2 items-center justify-end gap-2">
              <NotificationBell inboxHref="/dashboard/notifications/inbox" preferencesHref="/dashboard/notifications" />
            </div>
          )}
          <div className={fullBleed ? "flex-1 flex flex-col" : "flex-1 p-4 sm:p-6"}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
